import React, { useCallback, useEffect, useMemo } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { useDispatch, useSelector } from 'react-redux'
import { FundingProduct } from '@frontend/components/agromarket/core/access-control'
import classNames from 'classnames'
import type { IConsultingFormValues } from '@/components/composed/ConsultingForm/types'
import { createConsultingRequestBody } from '@/components/composed/ConsultingForm/utils'
import { useApplyFinancingOffer } from '@/components/composed/financing/hooks'
import type { IConsultingFormProps } from '@/components/composed/finansingConsulting/ConsultingForm/types'
import { ConsultingFormAgreement } from '@/components/composed/finansingConsulting/ConsultingFormAgreement'
import { openModal } from '@/components/composed/Modal/actions'
import type { IFinancingUxFeedbackModalProps } from '@/components/composed/modals/FinancingUxFeedbackModal/types'
import { Button } from '@/components/ui'
import { useAuthenticateUser } from '@/components/ui/AuthButton/hooks'
import { TextInput } from '@/components/ui/ReactHookFormFields'
import { getLocation } from '@/logic/app/reducer'
import { getUser, isUserAgrarian } from '@/logic/auth/reducer'
import { FINANCING_PRODUCT_BY_ROUTE } from '@/logic/financing/constants'
import { EGtmBlock } from '@/logic/metrika/financing/types'
import type { RootState } from '@/redux/interfaces'
import type { EFinancingOfferRoute } from '@/types'
import { EFinancingOfferType } from '@/types'
import { normalizePhoneNumber } from '@/utils/formatUtils'
import { isApplyingOfferRequestBody } from '@/utils/hooks/applyingFinancingOffer/typeguard'
import { useApplyingFinancingOfferForm } from '@/utils/hooks/applyingFinancingOffer/useApplyingFinancingOfferForm'
import { messages } from './messages'
import './styles.scss'

const mapState = (state: RootState) => ({
  isAgrarian: isUserAgrarian()(state),
  user: getUser()(state),
  location: getLocation()(state),
})

export const ConsultingForm: React.FC<IConsultingFormProps> = props => {
  const {
    submitButtonModifiers: submitButtonModifier = ['green'],
    inputPlaceholder = messages.form.phone.placeholder,
    inputModifiers,
    submitButtonClassname,
    className,
    children,
    requestMapper,
    isShowUxFeedback,
  } = props

  const { isAgrarian, user, location } = useSelector(mapState)
  const dispatch = useDispatch()
  const { isAuthenticated, openAuthModal } = useAuthenticateUser()
  const { applyFinancingOffer, isLoading, openSuccessModal } = useApplyFinancingOffer(EGtmBlock.CONSULTATION)

  const { setOptions, profile } = useApplyingFinancingOfferForm({
    productId: FundingProduct.MARKETPLACE,
    offerType: EFinancingOfferType.MARKETPLACE,
    requestMapper,
  })

  const initialValues: IConsultingFormValues = {
    loanAmount: '',
    purpose: '',
    phone: profile.phone,
    agreement: false,
  }

  const financingProductId = useMemo(() => {
    return FINANCING_PRODUCT_BY_ROUTE[location.pathname as EFinancingOfferRoute]
  }, [location.pathname])

  const { ...formMethods } = useForm<IConsultingFormValues>({ defaultValues: initialValues })
  const { control, handleSubmit, setValue } = formMethods

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => setValue('phone', normalizePhoneNumber(profile.phone)), [profile.phone])

  const sendForm = useCallback(
    async (values: IConsultingFormValues) => {
      const body = createConsultingRequestBody({ values, user, productId: financingProductId })

      if (isApplyingOfferRequestBody(body)) {
        const mappedBody = requestMapper?.(body) ?? body

        const fundingRequests = await applyFinancingOffer({ body: mappedBody, withSuccessModal: !isShowUxFeedback })
        const firstRequestId = fundingRequests?.[0]?.id
        const fundingProductsIds: FundingProduct[] = fundingRequests.map(({ id }) => id as unknown as FundingProduct)

        if (isShowUxFeedback && firstRequestId) {
          dispatch(
            openModal({
              options: {
                dialogId: 'FinancingUxFeedbackModal',
                dataModalTypeExtended: '--centered',
                shouldBlockedScroll: true,
                onCloseModal: () => openSuccessModal(firstRequestId, fundingProductsIds),
              },
              contentOptions: {
                isHasSkipButton: true,
                onSkipAction: () => openSuccessModal(firstRequestId, fundingProductsIds),
                onCustomSuccessAction: () => openSuccessModal(firstRequestId, fundingProductsIds),
              } as IFinancingUxFeedbackModalProps['options'],
            }),
          )
        }
      }
    },
    [applyFinancingOffer, dispatch, financingProductId, isShowUxFeedback, openSuccessModal, requestMapper, user],
  )

  const onSubmit = handleSubmit((values: IConsultingFormValues) => {
    if (isAuthenticated) {
      sendForm(values)
    } else {
      openAuthModal({
        shouldSendCodeImmediately: true,
        shouldSkipSelectRoleStep: true,
        initialValues: { phone: normalizePhoneNumber(values.phone) },
        onCloseDialog: () => {
          setOptions({ formValues: createConsultingRequestBody({ values, user, productId: financingProductId }) })
          sendForm(values)
        },
      })
    }
  })

  return (
    <form className={classNames(className, 'consultingForm')} onSubmit={onSubmit}>
      <FormProvider {...formMethods}>
        <TextInput
          name="phone"
          type="tel"
          placeholder={inputPlaceholder}
          rules={{ required: messages.form.phone.errors.required }}
          control={control}
          modifiers={inputModifiers}
          shouldBeTouchedBeforeError={false}
          isReadonly={isAuthenticated}
          isRequired
        />

        {children}

        <ConsultingFormAgreement />

        <Button
          className={submitButtonClassname}
          type="submit"
          modifiers={submitButtonModifier}
          isDisabled={!isAgrarian && isAuthenticated}
          loaderProps={{ isVisible: isLoading }}
        >
          {messages.form.submit}
        </Button>
      </FormProvider>
    </form>
  )
}
